import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, CalendarDays, Clock3, Search, Star } from "lucide-react";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import { applyLeaveRequest } from "../../../Moduels/leave/leaveApi";
import {
  decideCompOffCredit,
  fetchCompOffManage,
  fetchCompOffRoster,
  fetchCompOffSettings,
  fetchMyCompOff,
  processCompOffAttendance,
  requestCompOff,
} from "../../../Moduels/leave/compOffApi";
import { hasRole } from "../../../rbac/access";
import { useLeaveOrg } from "./useLeaveOrg";
import "./CompOffLeave.css";

const HISTORY_FILTERS = [
  ["all", "All"],
  ["approved", "Approved"],
  ["expiring", "Expiring"],
  ["expired", "Expired"],
  ["rejected", "Rejected"],
  ["capped", "Capped"],
];

const AUDIT_LABELS = {
  available: "Approved",
  partially_used: "Partly used",
  pending_approval: "Sent for review",
  rejected: "Rejected",
  expired: "Expired",
  capped: "Held at the balance cap",
  needs_review: "Flagged for review",
  reversed: "Reversed",
  adjustment: "Balance adjusted",
  consumed: "Used on approved leave",
  reserved: "Reserved for leave",
  released: "Leave reservation released",
  restored: "Credit restored",
};

const STATUS_LABEL = {
  available: "Approved",
  partially_used: "Used",
  consumed: "Used",
  pending_approval: "Pending",
  rejected: "Rejected",
  expired: "Expired",
  capped: "Capped",
  needs_review: "Needs review",
};

function apiMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || String(item)).join(" ");
  return err?.message || fallback;
}

function tidyQty(value) {
  if (value == null || value === "") return "0";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return String(num).replace(/(\d+)\.00\b/g, "$1").replace(/(\d+\.\d)0\b/g, "$1");
}

function tidyDate(value) {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const [year, month, day] = raw.split("-").map(Number);
  if (!year || !month || !day) return raw;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function tidyWhen(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return tidyDate(value);
  if (String(value).length <= 10) return tidyDate(value);
  return parsed.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function monthKey(value) {
  return String(value || "").slice(0, 7);
}

function todayIso() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function lastBuckets(count, mode) {
  const now = new Date();
  const rows = [];
  for (let index = count - 1; index >= 0; index -= 1) {
    if (mode === "week") {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - index * 7);
      const key = start.toISOString().slice(0, 10);
      rows.push({
        key,
        label: start.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
        match: (value) => {
          const raw = String(value || "").slice(0, 10);
          if (!raw) return false;
          const point = new Date(`${raw}T00:00:00`);
          const end = new Date(start);
          end.setDate(end.getDate() + 6);
          return point >= new Date(start.getFullYear(), start.getMonth(), start.getDate()) && point <= end;
        },
      });
    } else {
      const point = new Date(now.getFullYear(), now.getMonth() - index, 1);
      const key = `${point.getFullYear()}-${String(point.getMonth() + 1).padStart(2, "0")}`;
      rows.push({
        key,
        label: point.toLocaleDateString(undefined, { month: "short" }),
        match: (value) => monthKey(value) === key,
      });
    }
  }
  return rows;
}

function historyMatches(row, filter) {
  if (filter === "approved") return row.status === "available" || row.status === "partially_used";
  if (filter === "expiring") return Boolean(row.expiring);
  if (filter === "all") return true;
  return row.status === filter;
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part[0]).join("").toUpperCase() || "E";
}

function sumQty(rows, field) {
  return rows.reduce((total, row) => total + (Number(row?.[field]) || 0), 0);
}

function Badge({ status, expiring }) {
  const key = expiring ? "expiring" : status;
  return <span className={`col-badge is-${key}`}>{expiring ? "Expiring" : (STATUS_LABEL[status] || String(status || "").replace(/_/g, " "))}</span>;
}

function Person({ name, code, department }) {
  return (
    <div className="col-person">
      <span className="col-avatar" aria-hidden="true">{initials(name)}</span>
      <div>
        <strong>{name || "Employee"}</strong>
        <span>{[code, department].filter(Boolean).join(" · ") || "Employee"}</span>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, tone, label, value, note }) {
  return (
    <article className="col-stat">
      <span className={`col-stat-icon is-${tone}`} aria-hidden="true"><Icon size={18} /></span>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{note}</em>
      </div>
    </article>
  );
}

function Bars({ buckets, items }) {
  const series = buckets.map((bucket) => {
    const rows = items.filter((row) => !["rejected", "reversed"].includes(row.status) && bucket.match(row.earned_date || row.attendance_date));
    return {
      ...bucket,
      earned: sumQty(rows, "quantity"),
      used: sumQty(rows, "used_quantity"),
    };
  });
  const peak = Math.max(1, ...series.map((row) => Math.max(row.earned, row.used)));
  return (
    <div className="col-bars" aria-hidden="true">
      {series.map((row) => (
        <div className="col-bar-col" key={row.key}>
          <div className="col-bar-pair">
            <span style={{ height: `${Math.max(4, (row.earned / peak) * 100)}%` }} />
            <span className="is-used" style={{ height: `${Math.max(4, (row.used / peak) * 100)}%` }} />
          </div>
          <em>{row.label}</em>
        </div>
      ))}
    </div>
  );
}

export default function CompOffLeavePage() {
  const { role } = useContext(AuthContext);
  const { orgId } = useLeaveOrg();
  const staff = hasRole(role, ["admin", "hr", "manager"]);
  const canAdjust = hasRole(role, ["admin", "hr"]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [applyError, setApplyError] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "", half_day: false });
  const [pending, setPending] = useState(null);
  const [balances, setBalances] = useState(null);
  const [history, setHistory] = useState(null);
  const [trend, setTrend] = useState([]);
  const [roster, setRoster] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [historyEmployee, setHistoryEmployee] = useState("");
  const [historyFrom, setHistoryFrom] = useState("");
  const [historyTo, setHistoryTo] = useState("");
  const [historyFilter, setHistoryFilter] = useState("all");
  const [openAudit, setOpenAudit] = useState("");
  const [chartMode, setChartMode] = useState("month");
  const [busy, setBusy] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [query, setQuery] = useState("");
  const [department, setDepartment] = useState("");
  const [location, setLocation] = useState("");
  const [picked, setPicked] = useState([]);
  const [alloc, setAlloc] = useState({ days: "1", effective: todayIso(), expiry: "", reason: "" });
  const [allocNote, setAllocNote] = useState("");
  const [allocError, setAllocError] = useState("");
  const [allocating, setAllocating] = useState(false);

  const loadMine = useCallback(() => {
    setLoading(true);
    setError("");
    fetchMyCompOff()
      .then(setData)
      .catch((err) => setError(apiMessage(err, "Comp Off could not be loaded.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadMine(); }, [loadMine]);

  useEffect(() => {
    if (!canAdjust) return undefined;
    let active = true;
    fetchCompOffSettings()
      .then((next) => { if (active) setPolicy(next); })
      .catch(() => { if (active) setPolicy(null); });
    return () => { active = false; };
  }, [canAdjust]);

  useEffect(() => {
    if (!staff) return undefined;
    let active = true;
    Promise.all([
      fetchCompOffManage("pending", 1, 50),
      fetchCompOffManage("balances", 1, 200),
      fetchCompOffManage("history", 1, 200),
      fetchCompOffRoster(),
    ]).then(([nextPending, nextBalances, nextHistory, nextRoster]) => {
      if (!active) return;
      setPending(nextPending);
      setBalances(nextBalances);
      setTrend(nextHistory?.items || []);
      setHistory(nextHistory);
      setRoster(nextRoster);
    }).catch((err) => {
      if (active) setError(apiMessage(err, "Comp Off could not be loaded."));
    });
    return () => { active = false; };
  }, [staff, refreshKey]);

  useEffect(() => {
    if (!staff) return undefined;
    let active = true;
    fetchCompOffManage("history", 1, 200, historyEmployee || undefined)
      .then((next) => { if (active) setHistory(next); })
      .catch(() => { if (active) setHistory(null); });
    return () => { active = false; };
  }, [staff, historyEmployee, refreshKey]);

  const requested = useMemo(() => {
    if (!form.start_date || !form.end_date) return null;
    const start = new Date(`${form.start_date}T00:00:00`);
    const end = new Date(`${form.end_date}T00:00:00`);
    if (Number.isNaN(start.getTime()) || end < start) return null;
    if (form.half_day) return 0.5;
    return Math.round((end - start) / 86400000) + 1;
  }, [form]);

  const availableDays = Number(data?.summary?.available || 0);
  const overBalance = requested != null && requested > availableDays;
  const summary = data?.summary || {};
  const linkedEmployee = data?.employee?.name || "";
  const thisMonth = monthKey(todayIso());

  const orgStats = useMemo(() => {
    const rows = balances?.items || [];
    const withDays = rows.filter((row) => Number(row.available) > 0).length;
    const monthRows = trend.filter((row) => monthKey(row.earned_date) === thisMonth && !["rejected", "reversed"].includes(row.status));
    return {
      available: sumQty(rows, "available"),
      people: withDays || rows.length,
      pending: pending?.items?.length || 0,
      earned: sumQty(monthRows, "quantity"),
      used: sumQty(monthRows, "used_quantity"),
    };
  }, [balances, pending, trend, thisMonth]);

  const historyRows = useMemo(() => {
    return (history?.items || []).filter((row) => {
      if (!historyMatches(row, historyFilter)) return false;
      const day = String(row.earned_date || row.attendance_date || "").slice(0, 10);
      if (historyFrom && day < historyFrom) return false;
      if (historyTo && day > historyTo) return false;
      return true;
    });
  }, [history, historyFilter, historyFrom, historyTo]);

  const historyCounts = useMemo(() => {
    const items = history?.items || [];
    return Object.fromEntries(HISTORY_FILTERS.map(([key]) => [key, items.filter((row) => historyMatches(row, key)).length]));
  }, [history]);

  const rosterPeople = roster?.employees || [];
  const visiblePeople = rosterPeople.filter((person) => {
    const haystack = `${person.name} ${person.employee_code}`.toLowerCase();
    if (query && !haystack.includes(query.trim().toLowerCase())) return false;
    if (department && person.department !== department) return false;
    if (location && person.location !== location) return false;
    return true;
  });
  const selectedPeople = rosterPeople.filter((person) => picked.includes(person.employee_id));
  const allocDays = Number(alloc.days);
  const currentSelected = sumQty(selectedPeople, "available");
  const waitingCount = selectedPeople.filter((person) => !person.grants_immediately).length;
  const immediateCount = selectedPeople.length - waitingCount;

  const setLeaveField = (partial) => {
    setSubmitted(null);
    setApplyError("");
    setForm((current) => {
      const next = { ...current, ...partial };
      if (next.half_day) next.end_date = next.start_date;
      return next;
    });
  };

  const submitLeave = async (event) => {
    event.preventDefault();
    setApplyError("");
    setSubmitted(null);
    if (!data?.employee?.employee_id || !data?.leave_type_id) {
      setApplyError("Comp Off is not available as a leave type yet. Ask HR to enable it in Attendance settings.");
      return;
    }
    if (!form.start_date || !form.end_date) {
      setApplyError("Choose the leave dates.");
      return;
    }
    if (overBalance) {
      setApplyError("Requested days are more than the available Comp Off balance.");
      return;
    }
    const snapshot = {
      start: form.start_date,
      end: form.half_day ? form.start_date : form.end_date,
      days: requested,
      half: form.half_day,
      reason: (form.reason || "").trim(),
    };
    setSubmitting(true);
    try {
      await applyLeaveRequest({
        organisation_id: orgId,
        employee_id: data.employee.employee_id,
        leave_type_id: data.leave_type_id,
        start_date: snapshot.start,
        end_date: snapshot.end,
        reason: snapshot.reason || null,
        half_day: snapshot.half,
        half_day_session: snapshot.half ? "first_half" : null,
      });
      setSubmitted(snapshot);
      setForm({ start_date: "", end_date: "", reason: "", half_day: false });
      loadMine();
    } catch (err) {
      setApplyError(apiMessage(err, "The leave request could not be submitted."));
    } finally {
      setSubmitting(false);
    }
  };

  const review = async (creditId, decision) => {
    setBusy(true);
    setError("");
    setReviewNote("");
    try {
      await decideCompOffCredit(creditId, decision, decision === "approved" ? "Approved" : "Rejected");
      setReviewNote(decision === "approved"
        ? "Approved. Those days are now on the employee balance."
        : "Rejected. The days were not added.");
      setRefreshKey((value) => value + 1);
      loadMine();
    } catch (err) {
      setError(apiMessage(err, "The review could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  const syncMonth = async () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const iso = (value) => {
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${value.getFullYear()}-${month}-${day}`;
    };
    setBusy(true);
    setError("");
    try {
      await processCompOffAttendance({ date_from: iso(from), date_to: iso(to) });
      setRefreshKey((value) => value + 1);
      loadMine();
    } catch (err) {
      setError(apiMessage(err, "Attendance could not be synced for Comp Off."));
    } finally {
      setBusy(false);
    }
  };

  const togglePerson = (employeeId) => {
    setAllocNote("");
    setAllocError("");
    setPicked((current) => (
      current.includes(employeeId) ? current.filter((id) => id !== employeeId) : [...current, employeeId]
    ));
  };

  const submitAllocation = async (event) => {
    event.preventDefault();
    setAllocError("");
    setAllocNote("");
    const ids = staff ? picked : [data?.employee?.employee_id].filter(Boolean);
    if (!ids.length) {
      setAllocError(staff ? "Select at least one employee." : "Your employee profile is not linked to this login.");
      return;
    }
    if (!alloc.effective) {
      setAllocError("Choose the effective date.");
      return;
    }
    if (!alloc.reason.trim()) {
      setAllocError("Enter a reason or reference.");
      return;
    }
    setAllocating(true);
    try {
      const saved = await requestCompOff({
        employee_ids: ids,
        quantity: Number(alloc.days),
        effective_date: alloc.effective,
        expiry_date: alloc.expiry || null,
        reason: alloc.reason.trim(),
      });
      setAllocNote(saved?.message || "Comp Off request saved.");
      setAlloc((current) => ({ ...current, reason: "" }));
      setPicked([]);
      setRefreshKey((value) => value + 1);
      loadMine();
    } catch (err) {
      setAllocError(apiMessage(err, "The Comp Off request could not be saved."));
    } finally {
      setAllocating(false);
    }
  };

  const buckets = lastBuckets(chartMode === "week" ? 8 : 6, chartMode === "week" ? "week" : "month");

  return (
    <div className="col-page col-studio">
      {loading && !staff ? <p className="col-muted">Loading…</p> : null}
      {error ? <p className="col-alert" role="alert">{error}</p> : null}

      <section className="col-stats" aria-label="Comp Off summary">
        <Stat icon={CalendarDays} tone="blue" label="Available days" value={tidyQty(staff ? orgStats.available : summary.available)} note={staff ? `across ${orgStats.people} employees` : "ready to use"} />
        <Stat icon={Clock3} tone="amber" label="Pending review" value={tidyQty(staff ? orgStats.pending : summary.pending)} note={staff ? "require approval" : "with your manager"} />
        <Stat icon={Star} tone="violet" label="Earned this month" value={tidyQty(staff ? orgStats.earned : summary.total_earned)} note={staff ? "days" : "total earned"} />
        <Stat icon={BadgeCheck} tone="green" label="Days used" value={tidyQty(staff ? orgStats.used : summary.total_used)} note={staff ? "this month" : "from your balance"} />
      </section>

      {staff ? (
        <>
          <section className="col-panel col-chart-card" aria-label="Comp Off trend">
              <header className="col-panel-head">
                <div>
                  <h2>{chartMode === "week" ? "Weekly breakdown" : "Comp Off trend"}</h2>
                  <p>{chartMode === "week" ? "Earned and used days by week" : "Earned vs used days (last 6 months)"}</p>
                </div>
                <div className="col-seg" role="tablist" aria-label="Chart range">
                  <button type="button" className={chartMode === "month" ? "is-on" : ""} onClick={() => setChartMode("month")}>Trend overview</button>
                  <button type="button" className={chartMode === "week" ? "is-on" : ""} onClick={() => setChartMode("week")}>Weekly breakdown</button>
                </div>
              </header>
              <div className="col-legend"><span><i />Earned</span><span><i className="is-used" />Used</span></div>
              <Bars buckets={buckets} items={trend} />
            </section>

            <section className="col-panel col-table-card" aria-label="Pending approvals">
              <header className="col-panel-head">
                <div>
                  <h2>Pending approvals</h2>
                  <p>Reporting manager approval adds the days.</p>
                </div>
                {canAdjust ? (
                  <button type="button" className="col-text-btn" onClick={syncMonth} disabled={busy}>
                    {busy ? "Syncing…" : "Sync this month"}
                  </button>
                ) : null}
              </header>
              {reviewNote ? <p className="col-adjust-ok" style={{ margin: "10px 16px 0" }}>{reviewNote}</p> : null}
              {!pending?.items?.length ? (
                <p className="col-muted" style={{ padding: "16px 18px 18px" }}>No credits are waiting. A request from an employee or HR appears here for the reporting manager.</p>
              ) : (
                <div className="col-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Type</th>
                        <th className="col-num">Days</th>
                        <th>Requested on</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pending.items.map((row) => (
                        <tr key={row.credit_id}>
                          <td><Person name={row.employee_name} code={row.employee_code} department={row.department} /></td>
                          <td>{row.category_label || "Comp Off"}</td>
                          <td className="col-num">{tidyQty(row.quantity)}</td>
                          <td>{tidyDate(row.requested_on || row.earned_date)}</td>
                          <td>
                            <div className="col-act">
                              <button type="button" className="is-yes" disabled={busy} onClick={() => review(row.credit_id, "approved")}>Approve</button>
                              <button type="button" className="is-no" disabled={busy} onClick={() => review(row.credit_id, "rejected")}>Reject</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

          <div className="col-about-grid" aria-label="How Comp Off is added">
            <article className="col-about-card"><em>1</em><p>Attendance is marked on a holiday or weekly off, and the hours rule is met.</p></article>
            <article className="col-about-card"><em>2</em><p>Sync this month checks that attendance and creates the credit.</p></article>
            <article className="col-about-card"><em>3</em><p>Approve adds the days to the balance. Reject leaves them off. Both are saved in Credit history.</p></article>
          </div>
          {canAdjust ? <Link className="col-settings-link" to="/settings/attendance/comp-off">Comp Off approval settings</Link> : null}

          <section className="col-panel" id="comp-off-assign" aria-label="Allocate Comp Off">
            <header className="col-panel-head">
              <div>
                <h2>Allocate Comp Off</h2>
                <p>Assign days to employees. Employee and HR requests wait for the reporting manager.</p>
              </div>
            </header>
            <form className="col-alloc" onSubmit={submitAllocation}>
              <div>
                <div className="col-filters">
                  <label className="col-search">
                    Search employee
                    <Search size={15} aria-hidden="true" />
                    <input value={query} placeholder="Name, ID or mobile…" onChange={(event) => setQuery(event.target.value)} />
                  </label>
                  <label>
                    Department
                    <select aria-label="Department" value={department} onChange={(event) => setDepartment(event.target.value)}>
                      <option value="">All departments</option>
                      {(roster?.departments || []).map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                  </label>
                  <label>
                    Location
                    <select aria-label="Location" value={location} onChange={(event) => setLocation(event.target.value)}>
                      <option value="">All locations</option>
                      {(roster?.locations || []).map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                  </label>
                  <button className="col-text-btn" type="button" onClick={() => { setQuery(""); setDepartment(""); setLocation(""); }}>Reset</button>
                </div>
                <div className="col-pick-head">
                  <span>Select employees <em style={{ fontStyle: "normal", color: "#2563eb" }}>{picked.length} selected</em></span>
                  <button type="button" onClick={() => setPicked([])}>Clear all</button>
                </div>
                <div className="col-pick">
                  {!visiblePeople.length ? <p className="col-muted" style={{ padding: 14 }}>No employees match this search.</p> : visiblePeople.map((person) => {
                    const on = picked.includes(person.employee_id);
                    return (
                      <button type="button" key={person.employee_id} className={`col-pick-row${on ? " is-on" : ""}`} onClick={() => togglePerson(person.employee_id)}>
                        <input type="checkbox" readOnly checked={on} tabIndex={-1} aria-label={`Select ${person.name}`} />
                        <Person name={person.name} code={person.employee_code} department={person.department} />
                        <span className="col-balance-pill">{tidyQty(person.available)} days</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="col-alloc-form">
                <strong style={{ color: "#0f2744" }}>Allocation details</strong>
                <label>
                  Days to allocate
                  <input required type="number" min="0.5" step="0.5" value={alloc.days} onChange={(event) => setAlloc({ ...alloc, days: event.target.value })} />
                </label>
                <label>
                  Effective date
                  <input required type="date" value={alloc.effective} onChange={(event) => setAlloc({ ...alloc, effective: event.target.value })} />
                </label>
                <label>
                  Expiry date
                  <input type="date" min={alloc.effective || undefined} value={alloc.expiry} onChange={(event) => setAlloc({ ...alloc, expiry: event.target.value })} />
                </label>
                <label>
                  Reason / reference
                  <textarea rows={3} required placeholder="Manual allocation — HR" value={alloc.reason} onChange={(event) => setAlloc({ ...alloc, reason: event.target.value })} />
                </label>
                <div className="col-preview">
                  <strong>Balance preview</strong>
                  {!selectedPeople.length ? <p>Select employees to preview the new balance.</p> : (
                    <p>
                      Selected employees will receive {tidyQty(allocDays || 0)} day(s). Current balance {tidyQty(currentSelected)} day(s).
                      {immediateCount ? (immediateCount === 1 ? " This balance updates now." : ` ${immediateCount} balances update now.`) : ""}
                      {waitingCount ? (waitingCount === 1 ? " This request goes to the reporting manager and is added only after approval." : ` ${waitingCount} requests go to the reporting manager and are added only after approval.`) : ""}
                    </p>
                  )}
                </div>
                {allocNote ? <p className="col-adjust-ok">{allocNote}</p> : null}
                {allocError ? <p className="col-alert" role="alert">{allocError}</p> : null}
                <button className="col-alloc-submit" type="submit" disabled={allocating || !picked.length}>
                  {allocating ? "Saving…" : "Allocate Comp Off"}
                </button>
              </div>
            </form>
            <div className="col-source-note" style={{ margin: "0 16px 16px" }}>
              <div><strong>Manual allocation</strong>Raised by HR or an employee, then approved by the reporting manager before the days are added.</div>
              <div><strong>Attendance generated</strong>Created automatically from a worked holiday or weekly off, then reviewed in Pending approvals.</div>
            </div>
          </section>

          <section className="col-panel col-table-card" id="comp-off-history" aria-label="Comp Off credit history">
            <header className="col-panel-head">
              <div>
                <h2>Comp Off credit history</h2>
                <p>Earned, used, expired, and rejected credits.</p>
              </div>
            </header>
            <div className="col-filter-row">
              <label>
                Employee
                <select aria-label="Filter by employee" value={historyEmployee} onChange={(event) => { setOpenAudit(""); setHistoryEmployee(event.target.value); }}>
                  <option value="">All employees</option>
                  {(history?.employees || []).map((person) => (
                    <option key={person.employee_id} value={person.employee_id}>{person.name}</option>
                  ))}
                </select>
              </label>
              <label>
                From
                <input type="date" value={historyFrom} onChange={(event) => setHistoryFrom(event.target.value)} />
              </label>
              <label>
                To
                <input type="date" value={historyTo} onChange={(event) => setHistoryTo(event.target.value)} />
              </label>
            </div>
            <div className="col-chips" role="tablist" aria-label="Credit status" style={{ padding: "0 16px 12px" }}>
              {HISTORY_FILTERS.map(([key, label]) => (
                <button key={key} type="button" className={historyFilter === key ? "is-on" : ""} aria-pressed={historyFilter === key} onClick={() => setHistoryFilter(key)}>
                  {label}<em>{historyCounts[key] || 0}</em>
                </button>
              ))}
            </div>
            {!historyRows.length ? <p className="col-muted" style={{ padding: "0 16px 16px" }}>No Comp Off credits match this view.</p> : (
              <div className="col-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Worked on</th>
                      <th className="col-num">Credit</th>
                      <th className="col-num">Balance</th>
                      <th>Status</th>
                      <th>Expiry</th>
                      <th>Approved by</th>
                      <th>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((row) => {
                      const open = openAudit === row.credit_id;
                      return (
                        <Fragment key={row.credit_id}>
                          <tr>
                            <td><Person name={row.employee_name} code={row.employee_code} department={row.department} /></td>
                            <td>
                              <strong style={{ display: "block", fontWeight: 700 }}>{tidyDate(row.attendance_date)}</strong>
                              <span className="col-subline">{row.category_label}</span>
                            </td>
                            <td className="col-num">{tidyQty(row.quantity)} days</td>
                            <td className="col-num">{tidyQty(row.remaining_quantity)}</td>
                            <td><Badge status={row.status} expiring={row.expiring} /></td>
                            <td>{row.expiry_date ? tidyDate(row.expiry_date) : "Never"}</td>
                            <td>{row.approved_by_name || "—"}</td>
                            <td>
                              <button type="button" className="col-linkish is-quiet" onClick={() => setOpenAudit(open ? "" : row.credit_id)}>
                                {open ? "Hide" : "View"}
                              </button>
                            </td>
                          </tr>
                          {open ? (
                            <tr className="col-audit-row">
                              <td colSpan={8}>
                                {row.decision_reason ? <p className="col-audit-note">Note: {row.decision_reason}</p> : null}
                                {!row.audit?.length ? <p className="col-muted">No audit events recorded for this credit.</p> : (
                                  <ol className="col-audit">
                                    {row.audit.map((event, index) => (
                                      <li key={`${row.credit_id}-${index}`}>
                                        <span>{tidyWhen(event.at)}</span>
                                        <strong>{AUDIT_LABELS[event.action] || String(event.action || "").replace(/_/g, " ")}</strong>
                                        <span>{event.by || "System"}</span>
                                        <span>{event.reason || ""}</span>
                                      </li>
                                    ))}
                                  </ol>
                                )}
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          {policy?.enabled === false ? <p className="col-note">Comp Off is turned off. Turn it on before sync or allocation can add days.</p> : null}
        </>
      ) : (
        <>
          <section className="col-panel" aria-label="Request Comp Off">
            <header className="col-panel-head">
              <div>
                <h2>Request Comp Off</h2>
                <p>Your reporting manager is asked to approve. The days are added after they approve.</p>
              </div>
            </header>
            <form className="col-request" onSubmit={submitAllocation}>
              <div className="col-fields">
                <label>Days<input required type="number" min="0.5" step="0.5" value={alloc.days} onChange={(event) => setAlloc({ ...alloc, days: event.target.value })} /></label>
                <label>Effective date<input required type="date" value={alloc.effective} onChange={(event) => setAlloc({ ...alloc, effective: event.target.value })} /></label>
              </div>
              <label>Expiry date<input type="date" min={alloc.effective || undefined} value={alloc.expiry} onChange={(event) => setAlloc({ ...alloc, expiry: event.target.value })} /></label>
              <label>Reason<textarea required rows={3} value={alloc.reason} placeholder="Why you are requesting Comp Off" onChange={(event) => setAlloc({ ...alloc, reason: event.target.value })} /></label>
              {allocNote ? <p className="col-adjust-ok">{allocNote}</p> : null}
              {allocError ? <p className="col-alert" role="alert">{allocError}</p> : null}
              <button className="col-alloc-submit" type="submit" disabled={allocating || !data?.employee?.employee_id}>
                {allocating ? "Sending…" : "Send to reporting manager"}
              </button>
            </form>
          </section>

          {linkedEmployee ? (
            <section className="col-split">
              <form className="col-card" onSubmit={submitLeave}>
                <header className="col-card-head">
                  <div>
                    <h2>Apply leave</h2>
                    <p>{linkedEmployee}</p>
                  </div>
                </header>
                {submitted ? (
                  <div className="col-success" role="status">
                    <span className="col-success-mark" aria-hidden="true">✓</span>
                    <div>
                      <strong>Leave request submitted successfully</strong>
                      <p>The request is with the usual leave approval flow.</p>
                      <Link to="/leave?tab=approval">View leave status</Link>
                    </div>
                  </div>
                ) : null}
                <div className="col-fields">
                  <label>Start date<input type="date" value={form.start_date} onChange={(event) => setLeaveField({ start_date: event.target.value })} required /></label>
                  <label>End date<input type="date" value={form.end_date} onChange={(event) => setLeaveField({ end_date: event.target.value })} required disabled={form.half_day} /></label>
                </div>
                <label className="col-check"><input type="checkbox" checked={form.half_day} onChange={(event) => setLeaveField({ half_day: event.target.checked })} /> Half day</label>
                <label>Reason<textarea value={form.reason} onChange={(event) => setLeaveField({ reason: event.target.value })} rows={3} placeholder="Why you need this leave" /></label>
                {overBalance ? <p className="col-alert" role="alert">Requested days are more than the available balance of {tidyQty(summary.available)}.</p> : null}
                {applyError ? <p className="col-alert" role="alert">{applyError}</p> : null}
                <div className="col-form-actions">
                  <p>Requested <strong>{requested == null ? "—" : tidyQty(requested)}</strong> · Available <strong>{tidyQty(summary.available)}</strong></p>
                  <button className="col-btn" type="submit" disabled={submitting || !orgId || !data?.usage_allowed || overBalance}>
                    {submitting ? "Submitting…" : "Apply leave"}
                  </button>
                </div>
              </form>
              <section className="col-card">
                <header className="col-card-head"><div><h2>Leave history</h2><p>Requests already sent for this credit.</p></div></header>
                {!data?.applications?.length ? <p className="col-muted">No applications yet.</p> : (
                  <ul className="col-history">
                    {data.applications.map((row) => (
                      <li key={row.request_id}>
                        <span>{tidyDate(row.start_date)} – {tidyDate(row.end_date)}</span>
                        <span>{row.total_days} day(s)</span>
                        <Badge status={row.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </section>
          ) : null}

          <section className="col-panel col-table-card">
            <header className="col-panel-head"><div><h2>Earned credits</h2><p>Each row is one credit. Pending rows are waiting for your reporting manager.</p></div></header>
            {!data?.credits?.length ? <p className="col-muted" style={{ padding: 16 }}>No credits yet.</p> : (
              <div className="col-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Earned date</th>
                      <th>Reason</th>
                      <th className="col-num">Credit</th>
                      <th className="col-num">Remaining</th>
                      <th>Expiry</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.credits.map((row) => (
                      <tr key={row.credit_id}>
                        <td>{tidyDate(row.earned_date)}</td>
                        <td>{row.category_label}</td>
                        <td className="col-num">{tidyQty(row.quantity)}</td>
                        <td className="col-num">{tidyQty(row.remaining_quantity)}</td>
                        <td>{row.expiry_date ? tidyDate(row.expiry_date) : "Never"}</td>
                        <td><Badge status={row.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
